/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/deviceSipProfileApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API functions for Device SIP Profiles.
    Centralizes all API calls related to Device SIP Profiles.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  ListDeviceSipProfilesResponse,
  DeviceSipProfileResponse,
  DeviceSipProfilePayload,
} from '../types';

/**
 * Fetch list of device SIP profiles
 */
export async function fetchDeviceSipProfiles(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number | null;
}): Promise<ListDeviceSipProfilesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
    ...(params.domainId !== undefined && params.domainId !== null && { domainId: params.domainId.toString() }),
  });

  const response = await fetch(
    buildApiUrl(`/api/telephony/device-sip-profiles?${queryParams}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch device SIP profiles');
  }

  return await response.json();
}

/**
 * Fetch single device SIP profile by ID
 */
export async function fetchDeviceSipProfile(id: string | number): Promise<DeviceSipProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/device-sip-profiles/${id}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch device SIP profile');
  }

  return await response.json();
}

/**
 * Create device SIP profile
 */
export async function createDeviceSipProfile(
  payload: DeviceSipProfilePayload
): Promise<DeviceSipProfileResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/device-sip-profiles'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to create device SIP profile');
  }

  return await response.json();
}

/**
 * Update device SIP profile
 */
export async function updateDeviceSipProfile(
  id: string | number,
  payload: DeviceSipProfilePayload
): Promise<DeviceSipProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/device-sip-profiles/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to update device SIP profile');
  }

  return await response.json();
}

/**
 * Delete device SIP profile
 */
export async function deleteDeviceSipProfile(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/device-sip-profiles/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to delete device SIP profile');
  }
}

/**
 * Duplicate device SIP profile
 */
export async function duplicateDeviceSipProfile(
  id: string | number,
  name: string
): Promise<DeviceSipProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/device-sip-profiles/${id}/duplicate`),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({ name }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to duplicate device SIP profile');
  }

  return await response.json();
}

